import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Oval } from "react-loader-spinner";
import Navbar from "../../components/Navbar";
import { DEFAULT_ROUTE } from "../../routes/api/api_routes";

const QuizReview = () => {
  const navigate = useNavigate();
  const { quizId } = useParams();
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const quiz = location.state?.quiz;
  console.log("location.state:", location.state);
  console.log("quiz:", quiz);

  useEffect(() => {
    if (!quizId) {
      navigate("/quizz");
      return;
    }

    const questionsFromState = location.state?.questions || null;
    if (questionsFromState) {
      setAttempts(questionsFromState.map((q) => normalizeChoices(q)));
      setLoading(false);
      return;
    }

    const fetchAttempts = async () => {
      try {
        const res = await fetch(`${DEFAULT_ROUTE}quiz/${quizId}/review/`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `JWT ${localStorage.getItem("access")}`,
          },
        });

        if (!res.ok) throw new Error("Impossible de récupérer les résultats");
        const data = await res.json();
        const normalized = data.questions.map((q) => normalizeChoices(q));
        setAttempts(normalized);
      } catch (err) {
        alert("Erreur lors de la récupération du review.");
      } finally {
        setLoading(false);
      }
    };

    fetchAttempts();
  }, [quizId, navigate, location.state]);

  // ✅ Normalise user_choice_id pour toujours avoir un tableau [{id}]
  const normalizeChoices = (question) => {
    if (!Array.isArray(question.user_choice_id)) {
      if (question.user_choice_id == null) {
        question.user_choice_id = [];
      } else if (typeof question.user_choice_id === "number") {
        question.user_choice_id = [{ id: question.user_choice_id }];
      } else if (typeof question.user_choice_id === "string") {
        question.user_choice_id = [
          { id: parseInt(question.user_choice_id, 10) },
        ];
      }
    } else {
      // si c’est un tableau de nombres bruts [1,2], on mappe en [{id:1}, {id:2}]
      question.user_choice_id = question.user_choice_id.map((c) =>
        typeof c === "object" ? c : { id: c }
      );
    }
    return question;
  };

  const handleRedoQuiz = async () => {
    if (!quiz) {
      alert("Impossible de refaire le quiz : données manquantes.");
      navigate("/quizz");
      return;
    }

    try {
      const token = localStorage.getItem("access");
      const response = await fetch(`${DEFAULT_ROUTE}start-quiz/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `JWT ${token}`,
        },
        body: JSON.stringify({
          technology_id: quiz.technology,
          niveau: quiz.niveau,
          nombre_questions: 10,
        }),
      });

      if (!response.ok)
        throw new Error("Erreur lors de la création du nouveau quiz");
      const data = await response.json();
      navigate("/quizz/play", { state: { quiz: data } });
    } catch (error) {
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Oval height={50} width={50} color="#009688" />
      </div>
    );
  }

  if (!attempts.length) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center px-4">
        <p className="text-gray-500 text-lg text-center">
          Aucun résultat trouvé pour ce quiz.
        </p>
        <button
          onClick={() => navigate("/quizz")}
          className="mt-4 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full"
        >
          Retour à l'accueil
        </button>
      </div>
    );
  }

  const correctCount = attempts.filter((q) => {
    const userChoices = Array.isArray(q.user_choice_id)
      ? q.user_choice_id
      : q.user_choice_id != null
      ? [{ id: q.user_choice_id }]
      : [];

    return q.options.some((opt) =>
      userChoices.some((u) => u.id === opt.id && opt.is_correct)
    );
  }).length;

  const total = attempts.length;
  const percentage = Math.round((correctCount / total) * 100);

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />

      {/* Header responsive */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10 max-w-4xl mx-auto mb-8 mt-4 px-2">
        <button
          onClick={handleRedoQuiz}
          className="bg-teal-600 hover:bg-teal-700 text-white font-medium py-2 px-4 rounded-full w-full sm:w-auto"
        >
          Refaire un quiz
        </button>

        <div className="text-center w-full sm:w-auto">
          <h2 className="text-2xl font-bold text-orange-500">
            Résultats du quiz
          </h2>
          <p className="text-lg font-semibold text-green-600">
            Score : {correctCount}/{total} ({percentage}%)
          </p>
        </div>

        <button
          onClick={() => navigate("/quizz")}
          className="bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-full w-full sm:w-auto"
        >
          Nouveau quiz
        </button>
      </div>

      {/* Review des questions */}
      <div className="space-y-6 max-w-4xl mx-auto pb-4 px-2">
        {attempts.map((question, idx) => {
          const userCorrect = question.options.some((opt) =>
            question.user_choice_id.some(
              (u) => u.id === opt.id && opt.is_correct
            )
          );

          return (
            <div
              key={question.id}
              className="flex flex-col sm:flex-row rounded-2xl shadow-md overflow-hidden"
            >
              {/* Colonne gauche */}
              <div
                className={`flex sm:flex-col w-full sm:w-1/4 justify-between sm:justify-center items-center text-white p-4 sm:p-6 font-bold ${
                  userCorrect ? "bg-teal-600" : "bg-red-600"
                }`}
              >
                <p className="text-lg text-center sm:mb-2">
                  QUESTION {String(idx + 1).padStart(2, "0")}
                </p>
                <p
                  className={`px-3 py-1 rounded-full text-center text-sm sm:text-base ${
                    userCorrect
                      ? "text-teal-600 bg-white"
                      : "text-red-600 bg-white"
                  }`}
                >
                  {userCorrect ? "✔ Correct" : "✘ Incorrect"}
                </p>
              </div>

              {/* Colonne droite */}
              <div className="w-full sm:w-3/4 bg-white p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl mb-4 font-bold text-orange-500 text-center">
                  {question.html}
                </h2>

                <div className="space-y-2">
                  {question.options.map((option, i) => {
                    const letter = String.fromCharCode(65 + i);
                    const isCorrect = option.is_correct;
                    const isUser = question.user_choice_id.some(
                      (u) => u.id === option.id
                    );

                    return (
                      <div
                        key={option.id}
                        className={`px-3 py-2 rounded-md border flex justify-between items-center text-sm sm:text-base ${
                          isUser && isCorrect
                            ? "bg-teal-600 border-green-500 text-white"
                            : isUser && !isCorrect
                            ? "bg-red-500 border-white text-white"
                            : isCorrect
                            ? "bg-teal-600 border-green-400 text-white"
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <div className="flex items-center">
                          <span className="font-bold mr-2">{letter}.</span>
                          {option.html}
                        </div>
                        <div className="flex items-center space-x-2 text-xs sm:text-sm">
                          {isUser && !isCorrect && (
                            <span className="text-white font-medium">
                              Votre réponse
                            </span>
                          )}
                          {isCorrect && (
                            <span className="text-white font-medium">
                              Bonne réponse
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuizReview;
